
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

export const useLoginForm = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationNeeded, setVerificationNeeded] = useState(false);

  useEffect(() => {
    // Handle page load with error state or verification params
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    const verificationParam = urlParams.get('verification');
    const emailParam = urlParams.get('email');
    
    // If verification param exists, prefill the email
    if (verificationParam === '1' && emailParam) {
      setEmail(emailParam);
      setVerificationNeeded(true);
      setError("Please verify your email before logging in. Check your inbox or click 'Resend Verification Email'.");
    }
    else if (errorParam && errorDescription) {
      if (errorDescription.includes('Email not confirmed')) {
        setVerificationNeeded(true);
      } else {
        setError(decodeURIComponent(errorDescription));
      }
    }
  }, []);

  const getRedirectUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get('redirect');
    return redirectUrl ? decodeURIComponent(redirectUrl) : '/app/dashboard';
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      logger.debug({
        message: "Attempting login",
        context: { email }
      });
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        // Check if this is an email verification error
        if (error.message.includes('Email not confirmed')) {
          setVerificationNeeded(true);
          throw new Error('Your email has not been verified. Please check your inbox or click "Resend Verification Email".');
        }
        throw error;
      }

      if (data?.user) {
        // Successfully logged in
        logger.info({
          message: "User logged in successfully",
          context: { userId: data.user.id }
        });
        
        const redirectTo = getRedirectUrl();
        navigate(redirectTo);
      }
    } catch (error: any) {
      logger.error({
        message: "Login failed",
        error: error instanceof Error ? error : new Error(String(error)),
        context: { email }
      });
      
      setError(error.message);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError(null);
    
    try {
      // Use Supabase's built-in resend functionality
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
      });
      
      if (error) throw error;
      
      toast({
        title: "Verification email sent",
        description: "Please check your inbox for the verification link.",
      });
    } catch (error: any) {
      setError(error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setResendLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    loading,
    error,
    resendLoading,
    verificationNeeded,
    handleLogin,
    handleResendVerification
  };
};
